import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUploadField from "@/components/shared/ImageUploadField";
import { adminProvidersApi } from "@/lib/api/adminProviders";
import { categoriesApi } from "@/lib/api/categories";
import { providerTypesApi } from "@/lib/api/providerTypes";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const KYC_STATUSES = ["DRAFT", "PENDING", "APPROVED", "REJECTED"];

export default function EditProviderDialog({ open, onOpenChange, provider, onSaved }) {
    const { t, isRTL, lang } = useI18n();
    const [tree, setTree] = useState([]);
    const [providerTypes, setProviderTypes] = useState([]);
    const [form, setForm] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open && provider) {
            setForm({
                corporate_name: provider.corporate_name || "",
                type: provider.type || "CLINIC",
                description_en: provider.description_en || "",
                description_ar: provider.description_ar || "",
                logo_url: provider.logo_url || null,
                license_number: provider.license_number || "",
                license_expiry: provider.license_expiry || "",
                kyc_status: provider.kyc_status || "PENDING",
                is_visible: !!provider.is_visible,
                category_ids: provider.category_ids || [],
            });
            setError("");
            categoriesApi.listTree(false).then(setTree).catch(() => setTree([]));
            providerTypesApi.listPublic().then(setProviderTypes).catch(() => setProviderTypes([]));
        }
    }, [open, provider]);

    const allL2 = useMemo(() => {
        const out = [];
        const seen = new Set();
        (tree || []).forEach((l1) => {
            (l1.children || []).forEach((l2) => {
                if (!seen.has(l2.id)) {
                    seen.add(l2.id);
                    out.push({ ...l2, _parent_en: l1.name_en, _parent_ar: l1.name_ar });
                }
            });
        });
        return out;
    }, [tree]);

    if (!provider || !form) return null;

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));
    const toggleCategory = (id) => {
        setForm((p) => {
            const has = p.category_ids.includes(id);
            return { ...p, category_ids: has ? p.category_ids.filter((c) => c !== id) : [...p.category_ids, id] };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            const payload = {
                corporate_name: form.corporate_name.trim(),
                type: form.type,
                description_en: form.description_en.trim() || null,
                description_ar: form.description_ar.trim() || null,
                logo_url: form.logo_url || null,
                license_number: form.license_number.trim() || null,
                license_expiry: form.license_expiry || null,
                kyc_status: form.kyc_status,
                is_visible: form.is_visible,
                category_ids: form.category_ids,
            };
            const updated = await adminProvidersApi.update(provider.id, payload);
            toast.success(t("admin.providers.updated"));
            onSaved?.(updated);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto"
                data-testid="edit-provider-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t("admin.providers.editTitle")}</DialogTitle>
                    <DialogDescription>{provider.corporate_name}</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div className="flex items-start gap-4">
                        <ImageUploadField
                            value={form.logo_url}
                            onChange={(v) => update({ logo_url: v })}
                            shape="square"
                            maxDim={600}
                            testIdPrefix="edit-provider-logo"
                            label={t("admin.providers.fields.logoLabel")}
                        />
                        <div className="flex-1">
                            <Label htmlFor="ep_name">{t("admin.providers.fields.corporateName")}</Label>
                            <Input
                                id="ep_name" required
                                value={form.corporate_name}
                                onChange={(e) => update({ corporate_name: e.target.value })}
                                className="mt-1.5"
                                data-testid="edit-provider-name"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t("admin.providers.fields.type")}</Label>
                            <Select value={form.type} onValueChange={(v) => update({ type: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="edit-provider-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {providerTypes.map((typ) => (
                                        <SelectItem key={typ.code} value={typ.code}>
                                            {lang === "ar" ? typ.name_ar : typ.name_en}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t("admin.providers.fields.kycStatus")}</Label>
                            <Select value={form.kyc_status} onValueChange={(v) => update({ kyc_status: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="edit-provider-kyc">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {KYC_STATUSES.map((s) => (
                                        <SelectItem key={s} value={s}>{t(`admin.providers.kyc.${s}`)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ep_desc_en">{t("admin.providers.fields.descriptionEn")}</Label>
                            <Textarea
                                id="ep_desc_en" dir="ltr" rows={3}
                                value={form.description_en}
                                onChange={(e) => update({ description_en: e.target.value })}
                                className="mt-1.5"
                                data-testid="edit-provider-desc-en"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ep_desc_ar">{t("admin.providers.fields.descriptionAr")}</Label>
                            <Textarea
                                id="ep_desc_ar" dir="rtl" rows={3}
                                value={form.description_ar}
                                onChange={(e) => update({ description_ar: e.target.value })}
                                className="mt-1.5"
                                data-testid="edit-provider-desc-ar"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="ep_lic">{t("admin.providers.fields.licenseNumber")}</Label>
                            <Input
                                id="ep_lic" dir="ltr"
                                value={form.license_number}
                                onChange={(e) => update({ license_number: e.target.value })}
                                className="mt-1.5"
                                data-testid="edit-provider-license"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ep_exp">{t("admin.providers.fields.licenseExpiry")}</Label>
                            <Input
                                id="ep_exp" type="date"
                                value={form.license_expiry}
                                onChange={(e) => update({ license_expiry: e.target.value })}
                                className="mt-1.5"
                                data-testid="edit-provider-license-expiry"
                            />
                        </div>
                    </div>

                    <div>
                        <Label>{t("admin.providers.fields.categories")}</Label>
                        <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-border p-2 space-y-1">
                            {allL2.map((c) => {
                                const checked = form.category_ids.includes(c.id);
                                return (
                                    <label
                                        key={c.id}
                                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-sm ${checked ? "bg-secondary" : "hover:bg-muted/40"}`}
                                        data-testid={`edit-provider-cat-${c.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggleCategory(c.id)}
                                            className="h-4 w-4 accent-primary"
                                        />
                                        <span className="font-medium">
                                            {lang === "ar" ? (c.name_ar || c.name_en) : c.name_en}
                                        </span>
                                        <span className="ms-auto text-xs text-muted-foreground">
                                            {lang === "ar" ? c._parent_ar : c._parent_en}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        {form.category_ids.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {form.category_ids.map((id) => {
                                    const c = allL2.find((x) => x.id === id);
                                    if (!c) return null;
                                    return (
                                        <span
                                            key={id}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs"
                                        >
                                            {lang === "ar" ? (c.name_ar || c.name_en) : c.name_en}
                                            <button
                                                type="button"
                                                onClick={() => toggleCategory(id)}
                                                className="hover:bg-primary/20 rounded-full p-0.5"
                                                aria-label="remove"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-border p-3">
                        <div>
                            <Label className="cursor-pointer">{t("admin.providers.fields.isVisible")}</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {t("admin.providers.fields.isVisibleHint")}
                            </p>
                        </div>
                        <Switch
                            checked={form.is_visible}
                            onCheckedChange={(v) => update({ is_visible: v })}
                            data-testid="edit-provider-visible"
                        />
                    </div>

                    {provider.admin && (
                        <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                                {t("admin.providers.adminSection")}
                            </p>
                            <p className="text-sm mt-1">{provider.admin.full_name}</p>
                            <p className="text-xs text-muted-foreground font-mono mt-0.5">{provider.admin.email}</p>
                        </div>
                    )}

                    {error && (
                        <div
                            className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                            data-testid="edit-provider-error"
                        >
                            {error}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-provider-cancel">
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="edit-provider-save">
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
